import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Clock } from 'lucide-react';
import api from '../../lib/api';

export default function BlogTeaser() {
    const [posts, setPosts] = useState([]);
    useEffect(() => {
        api.get('/blog').then((r) => setPosts(r.data.items || [])).catch(() => {});
    }, []);
    if (!posts.length) return null;
    const featured = posts[0];
    const rest = posts.slice(1, 4);

    return (
        <section data-testid="home-blog-teaser" className="relative py-20 lg:py-28">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
                    <div>
                        <span className="dt-section-eyebrow">INSIGHTS</span>
                        <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
                            Field notes from <span className="dt-gradient-text">production AI</span>.
                        </h2>
                    </div>
                    <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-accent" data-testid="home-blog-view-all">
                        Read all articles <ArrowUpRight className="h-4 w-4" />
                    </Link>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <FeaturedCard post={featured} />
                    <div className="lg:col-span-5 grid grid-cols-1 gap-4">
                        {rest.map((p) => (
                            <SmallCard key={p.id} post={p} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function FeaturedCard({ post }) {
    return (
        <motion.article
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
        >
            <Link to={`/blog/${post.slug}`} data-testid={`blog-featured-${post.slug}`} className="group block">
                <div className="dt-neon-border">
                    <div className="dt-neon-inner overflow-hidden rounded-[var(--radius)]">
                        <div className="relative aspect-[16/9] overflow-hidden">
                            <img
                                src={post.cover_image}
                                alt={post.title}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(8,12,20,0.95)] via-[rgba(8,12,20,0.35)] to-transparent" />
                            <div className="absolute top-4 left-4 inline-flex items-center gap-2">
                                {post.tags?.slice(0, 2).map((t) => (
                                    <span key={t} className="font-mono text-[10px] tracking-[0.22em] uppercase text-primary bg-primary/10 border border-primary/30 px-2 py-0.5 rounded-full">
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="p-6">
                            <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight group-hover:text-primary transition-colors">
                                {post.title}
                            </h3>
                            <p className="mt-2 text-sm text-foreground/75 leading-relaxed line-clamp-2">{post.excerpt}</p>
                            <div className="mt-4 flex items-center gap-3 text-xs font-mono uppercase tracking-widest text-muted-foreground">
                                <Clock className="h-3.5 w-3.5" />
                                {post.reading_time_min} min read
                            </div>
                        </div>
                    </div>
                </div>
            </Link>
        </motion.article>
    );
}

function SmallCard({ post }) {
    return (
        <Link to={`/blog/${post.slug}`} data-testid={`blog-small-${post.slug}`} className="group block dt-glass rounded-xl p-4 hover:border-primary/40 transition-colors">
            <div className="flex gap-4">
                <div className="w-24 sm:w-28 aspect-square rounded-lg overflow-hidden border border-white/10 shrink-0">
                    <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="min-w-0">
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-primary">
                        {post.tags?.[0]}
                    </div>
                    <h4 className="font-display mt-1 text-sm sm:text-base font-semibold leading-snug group-hover:text-primary line-clamp-2">
                        {post.title}
                    </h4>
                    <div className="mt-2 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                        <Clock className="h-3 w-3" /> {post.reading_time_min} min
                    </div>
                </div>
            </div>
        </Link>
    );
}
